import React, { useState } from 'react';
import {
  Box, Typography, IconButton, Avatar, Menu, MenuItem,
  ListItemIcon, ListItemText, Collapse,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InstagramIcon from '@mui/icons-material/Instagram';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DirectionsIcon from '@mui/icons-material/Directions';
import StarIcon from '@mui/icons-material/Star';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { googleMapsUrl } from '../lists/SharedListPage';
import { formatDistance } from '../../utils/geo';
import { PHOTO_EXPIRY_MS } from '../../utils/apiUsage';
import { COLORS, FONT_DISPLAY } from '../../styles/theme';

export function isPhotoExpired(place) {
  if (!place.photoURL || !place.photoGeneratedAt) return !!place.placeId;
  return Date.now() - place.photoGeneratedAt > PHOTO_EXPIRY_MS;
}

const CATEGORY_DOTS = {
  mustgo: COLORS.accent,
  luxury: COLORS.gold,
  healthy: COLORS.sage,
};

export default function PlaceCard({ place, selected, onClick, onEdit, onDelete, onToggleVisited, compact, distanceKm }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [captionCopied, setCaptionCopied] = useState(false);
  const dotColor = CATEGORY_DOTS[place.category];
  const displayName = (place.name && place.name.trim()) || 'Untitled';

  const metaBits = [
    place.cuisine,
    distanceKm != null ? formatDistance(distanceKm) : null,
    place.address ? place.address.split(',')[0].trim() : null,
  ].filter(Boolean);

  const notePreview = place.visitedNotes || place.remarks || null;

  const handleCopyCaption = async (e) => {
    e.stopPropagation();
    if (!place.instagramCaption) return;
    try {
      await navigator.clipboard.writeText(place.instagramCaption);
      setCaptionCopied(true);
      setTimeout(() => setCaptionCopied(false), 1500);
    } catch (_) {}
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        py: compact ? 1.1 : 1.3,
        pr: 1,
        transition: 'background-color .14s',
        '&:hover': { bgcolor: 'rgba(244,239,233,0.025)' },
        ...(selected && {
          '&::before': {
            content: '""', position: 'absolute',
            left: 0, top: 10, bottom: 10, width: 2,
            borderRadius: 1, bgcolor: COLORS.accent,
          },
        }),
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        {/* Photo — square-rounded, slightly larger than before, no border */}
        <Avatar
          src={place.photoURL || undefined}
          variant="rounded"
          sx={{
            width: compact ? 58 : 76,
            height: compact ? 58 : 76,
            borderRadius: 2.5,
            flexShrink: 0,
            bgcolor: 'rgba(227,105,80,0.09)',
            color: COLORS.accent,
            fontSize: '1.1rem',
            // Visited places get a quiet desat so to-try reads first
            filter: place.visited ? 'saturate(0.78) brightness(0.9)' : 'none',
          }}
        >
          {!place.photoURL && '◴'}
        </Avatar>

        {/* Title column */}
        <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
          {/* Name row: priority dot · name · rating · more */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
            {dotColor && (
              <Box sx={{
                width: 7, height: 7, borderRadius: '50%',
                bgcolor: dotColor, flexShrink: 0,
                alignSelf: 'center', mt: -0.3,
              }} />
            )}
            <Typography noWrap sx={{
              flex: 1,
              fontFamily: FONT_DISPLAY,
              fontWeight: 600,
              fontSize: compact ? '0.98rem' : '1.08rem',
              lineHeight: 1.2,
              letterSpacing: '-0.015em',
              color: place.name ? COLORS.textPrimary : COLORS.textSecondary,
              fontStyle: place.name ? 'normal' : 'italic',
            }}>
              {displayName}
            </Typography>
            {place.visited && place.visitedRating != null && (
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.25,
                color: COLORS.gold, fontWeight: 600, fontSize: '0.78rem',
                flexShrink: 0,
              }}>
                <StarIcon sx={{ fontSize: 11 }} />
                <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {place.visitedRating.toFixed ? place.visitedRating.toFixed(1) : place.visitedRating}
                </Box>
              </Box>
            )}
            {place.visited && place.visitedRating == null && (
              <CheckCircleIcon sx={{ fontSize: 13, color: COLORS.accent, flexShrink: 0 }} />
            )}
            {!compact && (
              <IconButton
                size="small"
                onClick={e => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
                sx={{
                  color: COLORS.textTertiary, p: 0.25, mr: -0.5,
                  '&:hover': { color: COLORS.textSecondary, bgcolor: 'transparent' },
                }}
              >
                <MoreHorizIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Metadata line */}
          {metaBits.length > 0 && (
            <Typography noWrap sx={{
              fontSize: '0.76rem', color: COLORS.textSecondary,
              fontWeight: 400, mt: 0.3, letterSpacing: '-0.003em',
            }}>
              {metaBits.join('  ·  ')}
            </Typography>
          )}

          {/* Quiet note preview + expand chevron.
              One line, italic, tap chevron to see more. */}
          {(notePreview || place.instagramCaption) && (
            <Box
              onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
              sx={{
                mt: 0.5,
                display: 'flex', alignItems: 'center', gap: 0.5,
                cursor: 'pointer',
                color: COLORS.textTertiary,
                '&:hover': { color: COLORS.textSecondary },
              }}
            >
              <Typography noWrap sx={{
                flex: 1, fontSize: '0.74rem', fontStyle: 'italic',
                letterSpacing: '-0.003em',
              }}>
                {notePreview || place.instagramCaption.split('\n').find(Boolean)}
              </Typography>
              <ExpandMoreIcon sx={{
                fontSize: 14, transition: 'transform .2s',
                transform: expanded ? 'rotate(180deg)' : 'none',
              }} />
            </Box>
          )}

          {place.instagram && !notePreview && !place.instagramCaption && (
            // Only surface the IG mark on cards with no other preview, so the
            // card bottom isn't visually empty
            <Box
              component="a"
              href={place.instagram}
              target="_blank"
              rel="noopener"
              onClick={e => e.stopPropagation()}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.3,
                mt: 0.5, textDecoration: 'none',
                color: COLORS.textTertiary,
                '&:hover': { color: COLORS.textSecondary },
              }}
            >
              <InstagramIcon sx={{ fontSize: 11 }} />
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 500 }}>Instagram</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Expanded detail panel */}
      <Collapse in={expanded}>
        <Box sx={{
          pl: compact ? 8.5 : 11, pr: 1, pt: 0.75,
          display: 'flex', flexDirection: 'column', gap: 0.75,
        }}>
          {place.visitedNotes && (
            <Typography sx={{
              fontSize: '0.78rem', color: COLORS.textSecondary,
              fontStyle: 'italic', lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}>
              <StarIcon sx={{ fontSize: 10, color: COLORS.gold, mr: 0.4, verticalAlign: 'middle' }} />
              {place.visitedNotes}
            </Typography>
          )}
          {place.remarks && (
            <Typography sx={{
              fontSize: '0.78rem', color: COLORS.textSecondary,
              lineHeight: 1.5, whiteSpace: 'pre-wrap',
            }}>
              {place.remarks}
            </Typography>
          )}
          {place.instagramCaption && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.35 }}>
                <InstagramIcon sx={{ fontSize: 10, color: COLORS.rose }} />
                <Typography variant="overline" sx={{ color: COLORS.rose, fontSize: '0.6rem' }}>
                  Caption
                </Typography>
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" onClick={handleCopyCaption}
                  sx={{ p: 0.25, color: captionCopied ? COLORS.accent : COLORS.textTertiary }}>
                  {captionCopied
                    ? <CheckIcon sx={{ fontSize: 12 }} />
                    : <ContentCopyIcon sx={{ fontSize: 11 }} />}
                </IconButton>
              </Box>
              <Typography sx={{
                display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical',
                overflow: 'hidden', whiteSpace: 'pre-wrap',
                fontSize: '0.76rem', color: COLORS.textSecondary, lineHeight: 1.55,
              }}>
                {place.instagramCaption}
              </Typography>
            </Box>
          )}
          {place.instagram && (
            <Box
              component="a"
              href={place.instagram}
              target="_blank"
              rel="noopener"
              onClick={e => e.stopPropagation()}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.4,
                textDecoration: 'none', color: COLORS.textSecondary,
                '&:hover': { color: COLORS.textPrimary },
              }}
            >
              <InstagramIcon sx={{ fontSize: 12 }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 500 }}>Open on Instagram</Typography>
            </Box>
          )}
        </Box>
      </Collapse>

      {/* Overflow menu */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        onClick={e => e.stopPropagation()}
        slotProps={{ paper: { sx: { minWidth: 190 } } }}
      >
        <MenuItem dense onClick={() => { setMenuAnchor(null); onToggleVisited(place); }}>
          <ListItemIcon sx={{ minWidth: 28 }}>
            {place.visited
              ? <CheckCircleIcon sx={{ fontSize: 17, color: COLORS.accent }} />
              : <CheckCircleOutlineIcon sx={{ fontSize: 17, color: COLORS.textSecondary }} />}
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.84rem' }}>
            {place.visited ? 'Mark as not visited' : 'Mark as visited'}
          </ListItemText>
        </MenuItem>
        {(place.address || place.placeId) && (
          <MenuItem dense component="a" href={googleMapsUrl(place)} target="_blank" rel="noopener"
            onClick={() => setMenuAnchor(null)}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              <DirectionsIcon sx={{ fontSize: 17, color: COLORS.textSecondary }} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.84rem' }}>Open in Maps</ListItemText>
          </MenuItem>
        )}
        <MenuItem dense onClick={() => { setMenuAnchor(null); onEdit(place); }}>
          <ListItemIcon sx={{ minWidth: 28 }}>
            <EditIcon sx={{ fontSize: 16, color: COLORS.textSecondary }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.84rem' }}>Edit</ListItemText>
        </MenuItem>
        <MenuItem dense onClick={() => { setMenuAnchor(null); onDelete(place); }}
          sx={{ color: '#d16f6f' }}>
          <ListItemIcon sx={{ minWidth: 28 }}>
            <DeleteOutlineIcon sx={{ fontSize: 17, color: '#d16f6f' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.84rem' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
